import { useEffect, useRef, useState } from "react";
import { BiReset } from "react-icons/bi";
import { IoChevronForwardCircleOutline } from "react-icons/io5";
import { LuCalendarDays, LuPackageSearch } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { RxCrossCircled } from "react-icons/rx";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import { VolumeForecastDropdownModel, VolumeForecastSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/VolumeForecastSlicer.model";
import { TOAST_CONTAINER_ID, VOLUME_FORECAST_TAB_ID } from "../../../../shared/constants";
import "../../../../slicer-styles.css";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { volumeForecastTabActions } from "../../../../store/slices/volume-forecast-tab-slice";
import "../../../../styles.css";
import RadioDropdown from "../../../UI/RadioDropdown/RadioDropdown";
import CheckboxDropdown from "../../../UI/CheckboxDropdown/CheckboxDropdown";
import { getDefaultSlicersForForecastEntryApp, getToastOptions } from "../../../../shared/functions";
import Modal from "../../../UI/Modal/Modal";

const VolumeForecastSlicers = (props: {
    isGridChanged: boolean;
}) => {
    // States
    const [periods, setPeriods] = useState(Array.from({ length: 13 }, (_, i) => ({ name: (i + 1).toString(), value: (i + 1).toString() })));
    const [slicerChangeAction, setSlicerChangeAction] = useState<"apply" | "reset">("apply");

    // Redux variables
    const dispatchFn = useDispatch();
    const slicers: VolumeForecastDropdownModel = useSelector((state: RootState) => state.volumeForecastTab.slicers);
    const tempSelectedSlicers: VolumeForecastSelectedSlicerModel = useSelector((state: RootState) => state.volumeForecastTab.tempSelectedSlicers);
    const selectedQuarters = useSelector((state: RootState) => state.volumeForecastTab.selectedQuarters);
    const selectedPeriods = useSelector((state: RootState) => state.volumeForecastTab.selectedPeriods);
    const selectedHierarchies = useSelector((state: RootState) => state.volumeForecastTab.selectedHierarchies);
    const [isSlicerChanged, setIsSlicerChanged] = useState<Boolean>(false);

    // Variables
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const slicerChangeModalId: string = "volumeForecastSlicerChangeModal";
    const quarters = [{ name: 'Quarter 1', value: "Quarter 1" }, { name: 'Quarter 2', value: "Quarter 2" },
    { name: 'Quarter 3', value: "Quarter 3" }, { name: 'Quarter 4', value: "Quarter 4" }];

    /**
     * Handler for applying selected slicers.
     * @param {Event} event Apply filters button click event.
     * @returns {void}
     */
    const applySelectedSlicers = (event: any): void => {
        dispatchFn(volumeForecastTabActions.setSelectedSlicers(null));
        dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
            [VOLUME_FORECAST_TAB_ID]: false
        }));
        setIsSlicerChanged(false);

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    /**
     * Handler for resetting selected slicers.
     * @param {Event} event Reset filters button click event.
     * @returns {void}
     */
    const resetSelectedSlicers = (event: any) => {
        const defaultSlicers: VolumeForecastSelectedSlicerModel = getDefaultSlicersForForecastEntryApp(VOLUME_FORECAST_TAB_ID);

        if (
            tempSelectedSlicers.sku !== defaultSlicers.sku ||
            tempSelectedSlicers.year !== defaultSlicers.year ||
            selectedQuarters.toSorted().toString() !== ["All"].toString() ||
            selectedPeriods.toSorted().toString() !== ["All"].toString() ||
            selectedHierarchies.product !== "Brand-Sub Brand-Volume Size"  || 
            selectedHierarchies.timeline !== "Weekly"
        ) {
            dispatchFn(volumeForecastTabActions.resetSelectedSlicers(null));
            dispatchFn(volumeForecastTabActions.setQuarters(["All"]));
            dispatchFn(volumeForecastTabActions.setPeriods(["All"]));
            dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                [VOLUME_FORECAST_TAB_ID]: false
            }));
            dispatchFn(volumeForecastTabActions.setSelectedHierarchies({
                product: 'Brand-Sub Brand-Volume Size',
                timeline: 'Weekly'
            }));

            setIsSlicerChanged(false);
        } else {
            toast.info("No filters have been changed to reset", {
                ...getToastOptions("info"),
                containerId: TOAST_CONTAINER_ID
            });
        }

        if (props.isGridChanged) {
            modalCloseBtnRef.current?.click();
        }
    };

    const dropdownItemChangeHandler = (slicer_name: string, dropdownItemValue: any) => {
        dispatchFn(volumeForecastTabActions.setTempSlicers({
            ...tempSelectedSlicers,
            [slicer_name]: dropdownItemValue
        }));
        setIsSlicerChanged(true);
    };

    function getFilteredList(selectedValues: any) {
        // Define the lists
        const list1 = [{ name: '1', value: '1' }, { name: '2', value: '2' }, { name: '3', value: '3' }];
        const list2 = [{ name: '4', value: '4' }, { name: '5', value: '5' }, { name: '6', value: '6' }];
        const list3 = [{ name: '7', value: '7' }, { name: '8', value: '8' }, { name: '9', value: '9' }];
        const list4 = [{ name: '10', value: '10' }, { name: '11', value: '11' }, { name: '12', value: '12' }, { name: '13', value: '13' }];

        let result: any = [];

        // Check selected values and push corresponding lists to the result
        if (selectedValues.includes('Quarter 1')) {
            result = result.concat(list1);  // Add list 1-3
        }
        if (selectedValues.includes('Quarter 2')) {
            result = result.concat(list2);  // Add list 4-6
        }
        if (selectedValues.includes('Quarter 3')) {
            result = result.concat(list3);  // Add list 7-9
        }
        if (selectedValues.includes('Quarter 4')) {
            result = result.concat(list4);  // Add list 10-13
        }

        return result;
    }

    const checkboxDropdownItemChangeHandler = (slicer_name: string, dropdownItemValue: any) => {
        if (slicer_name === 'quarters') {
            dispatchFn(volumeForecastTabActions.setQuarters(dropdownItemValue))
        } else if (slicer_name === 'periods') {
            dispatchFn(volumeForecastTabActions.setPeriods(dropdownItemValue))
        } else {
            dispatchFn(volumeForecastTabActions.setTempSlicers({
                ...tempSelectedSlicers,
                [slicer_name]: Number(dropdownItemValue)
            }));
        }

        setIsSlicerChanged(true);
    };

    /**
     * Handler to close slicer change modal
     */
    const closeSlicerChangeModal = (event: any) => {
        modalCloseBtnRef.current?.click();
    };

    // Slicer Change Modal body
    const slicerChangeModalBody: JSX.Element = (
        <div className="row">
            <div className="col col-12 mb-2">
                {`${(slicerChangeAction === "apply") ? "Applying" : "Resetting"} filters will discard the changes done to the grid. Do you want to proceed?`}
            </div>
            <div className="col col-6">
                <button className="btn btn-warning w-100" onClick={closeSlicerChangeModal}>
                    <span className="fs-5">
                        <RxCrossCircled />
                    </span> No
                </button>
            </div>
            <div className="col col-6">
                <button className="btn btn-success w-100" onClick={slicerChangeAction === "apply" ? applySelectedSlicers : resetSelectedSlicers}>
                    <span className="fs-5">
                        <IoIosCheckmarkCircleOutline />
                    </span> Yes
                </button>
            </div>
        </div>
    );

    const product = selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? "sku" : selectedHierarchies.product === "Brand-Sub Brand" ? "brandSubBrand": "brand";
    const productLabel = selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? "Product" : selectedHierarchies.product === "Brand-Sub Brand" ? "Brand-Sub Brand": "Brand";
    const selectedValue = selectedHierarchies.product === "Brand-Sub Brand-Volume Size" ? tempSelectedSlicers.sku : 
                        selectedHierarchies.product === "Brand-Sub Brand" ? tempSelectedSlicers.brandSubBrand : tempSelectedSlicers.brand;

    useEffect(() => {
        setPeriods(getFilteredList(selectedQuarters));
    }, [selectedQuarters]);

    return (
        <>
            <RadioDropdown
                id="pivoted-part-code-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Product"
                selectedValue={selectedValue}
                items={slicers.sku}
                onItemChanged={(value: any) => dropdownItemChangeHandler(product, value)}
            />

            <RadioDropdown
                id="pivoted-year-dropdown"
                icon={<LuCalendarDays size={20} />}
                label="Year"
                selectedValue={tempSelectedSlicers.year.toString()}
                items={slicers.years}
                onItemChanged={(value: any) => dropdownItemChangeHandler("year", value)}
            />

            <CheckboxDropdown
                id="pivoted-quarters-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Quarters"
                selectedValues={selectedQuarters}
                items={quarters}
                onItemChanged={(value: any) => checkboxDropdownItemChangeHandler("quarters", value)}
            />

            <CheckboxDropdown
                id="pivoted-periods-dropdown"
                icon={<LuPackageSearch size={20} />}
                label="Periods"
                selectedValues={selectedPeriods}
                items={periods}
                onItemChanged={(value: any) => checkboxDropdownItemChangeHandler("periods", value)}
            />

            <div className="d-inline me-1">
                <button type="button" className="btn btn-sm text-white" style={{ backgroundColor: "#00833E" }} onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("apply") : applySelectedSlicers(e)
                } disabled={!isSlicerChanged} {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <IoChevronForwardCircleOutline size={20} /> &nbsp;Apply filters
                    </div>
                </button>
            </div>

            <div className="d-inline">
                <button type="button" className="btn btn-sm btn-warning" onClick={
                    (e) => props.isGridChanged ? setSlicerChangeAction("reset") : resetSelectedSlicers(e) 
                } {...(props.isGridChanged ? { "data-bs-toggle": "modal", "data-bs-target": `#${slicerChangeModalId}` } : {})}>
                    <div className="d-flex justify-content-center align-items-center">
                        <BiReset size={20} /> &nbsp;Reset filters
                    </div>
                </button>
            </div>

            {
                createPortal(
                    <Modal
                        id={slicerChangeModalId}
                        ref={modalCloseBtnRef}
                        title={`${(slicerChangeAction === "apply") ? "Apply" : "Reset"} Filters?`}
                        onModalClose={() => { }}
                    >
                        {slicerChangeModalBody}
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default VolumeForecastSlicers;
